/**
 * Ficha única de un producto de proveedor: contenido AiPeces y datos que manda la sync.
 */

import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { Copy, ExternalLink, Loader2, Save } from 'lucide-react';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { ResourceDetail } from '@/components/shared/ResourceDetail';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, formatEur } from '@/lib/format-es';
import { notify } from '@/lib/notify';
import { storeProductsListHrefFrom } from '@/lib/store-products-return';
import { STATUS_TONE_VARIANT, storeStatusLabel } from '@/lib/store-status';
import { useLocation, useParams } from 'react-router-dom';
import { shippingPackagePatchValue, shippingPackageSelectValue } from '../catalog/ShippingPackageField';
import {
    useCatalogProductDetail,
    useSetCatalogPriceOverride,
    useSetCatalogProductActive,
    useUpdateCatalogProductContent,
} from '../catalog/hooks/useStoreCatalog';
import type { CatalogContentPatch, CatalogFaqItem, CatalogProductDetail, CatalogQualityStatus } from '../catalog/types';
import { catalogSyncStaleMaxHours, isStaleSync } from '../catalog/catalogSyncFreshness';
import { BrandCompliance, CatalogProductBrandField } from './components/CatalogProductBrandField';
import { FichaCompleteness } from './components/FichaCompleteness';
import { FichaFieldNotices } from './components/FichaFieldNotices';
import { FichaStatusBadge } from './components/FichaStatusBadge';
import { CatalogAvailabilityEditor } from './components/CatalogAvailabilityEditor';
import { ProductFichaPreview } from './components/ProductFichaPreview';
import { SupplierMentionWarning } from './components/SupplierMentionWarning';
import { useSupplierMentionTerms } from './hooks/useSupplierMentionTerms';
import { catalogImageUrl, formatProductPrice, publicProductUrl } from './lib/catalogImageUrl';
import { findSupplierMention, patchHasInternalLeak } from './lib/editorialLeak';
import { assessFichaFields, fichaSaveBlocked, noticesForField } from './lib/fichaFieldLimits';
import { buildProductAiPrompt } from './lib/productFichaTemplate';
import { QUALITY_HINTS, QUALITY_LABELS, QUALITY_ORDER } from './lib/qualityLabels';
import {
    contentDraftIsDirty,
    contentDraftSnapshot,
    omnibusMinPrice,
    publishedBrand,
    type ContentDraft,
} from '../products/productDetailModel';
import { stockLabel } from '../products/productListModel';
import { useWarnUnsavedLeave } from '../products/useWarnUnsavedLeave';

type FormState = ContentDraft;
type ConfirmKind = 'activate' | 'deactivate' | 'price-set' | 'price-unlock';

function asStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.map((item) => String(item ?? '').trim()).filter(Boolean);
}

function asSpecs(raw: unknown): Record<string, string> {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    return Object.fromEntries(
        Object.entries(raw as Record<string, unknown>).map(([key, value]) => [key, String(value ?? '')]),
    );
}

function specsToRows(specs?: Record<string, string>): Array<{ key: string; value: string }> {
    const entries = Object.entries(specs || {});
    if (entries.length === 0) return [{ key: '', value: '' }];
    return entries.map(([key, value]) => ({ key, value: String(value ?? '') }));
}

function rowsToSpecs(rows: Array<{ key: string; value: string }>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const row of rows) {
        const key = row.key.trim();
        if (!key) continue;
        out[key] = row.value;
    }
    return out;
}

function splitList(raw: string): string[] {
    return raw.split(/[,;]/).map((item) => item.trim()).filter(Boolean);
}

function attributeText(raw: unknown): string {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return '';
    return Object.entries(raw as Record<string, unknown>)
        .map(([key, value]) => `${key}: ${String(value ?? '')}`)
        .join(' · ');
}

function buildForm(detail: CatalogProductDetail): FormState {
    const packageValue = shippingPackageSelectValue(detail.shippingPackageType);
    return {
        nombre: detail.nombre ?? '',
        editorial: detail.editorial ?? '',
        veredicto: detail.veredictoAipeces ?? '',
        descripcionCorta: detail.descripcionCorta ?? '',
        descripcionLarga: detail.descripcionLarga ?? '',
        seoTitle: detail.seoTitle ?? '',
        seoDescription: detail.seoDescription ?? '',
        imagenAlt: detail.imagenAlt ?? '',
        badges: asStringArray(detail.badges).join(', '),
        pescaTags: asStringArray(detail.pescaTags).join(', '),
        especies: asStringArray(detail.especiesRelacionadas).join(', '),
        guias: asStringArray(detail.guiasRelacionadas).join(', '),
        qualityStatus: (detail.qualityStatus as CatalogQualityStatus) || 'draft',
        shippingPackageType: packageValue,
        shippingPackageLoaded: packageValue,
        devolucionRecogidaSinCoste: detail.devolucionRecogidaSinCoste === true,
        faq: (detail.faq && detail.faq.length > 0 ? detail.faq : [{ q: '', a: '' }]).map((item) => ({
            q: item.q || item.question || '',
            a: item.a || item.answer || '',
        })),
        specs: specsToRows(asSpecs(detail.specs)),
        categoriaRuta: detail.categoriaRuta ?? '',
        coleccionSlugs: (detail.colecciones ?? []).map((item) => item.slug).filter(Boolean).join(', '),
    };
}

function contentValues(form: FormState): string[] {
    return [
        form.nombre, form.editorial, form.veredicto, form.descripcionCorta,
        form.descripcionLarga, form.seoTitle, form.seoDescription, form.imagenAlt,
        form.badges, form.pescaTags, form.especies, form.guias,
        form.faq.map((item) => `${item.q} ${item.a}`).join('\n'),
        form.specs.map((row) => `${row.key} ${row.value}`).join('\n'),
    ];
}

function SideBlock({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="space-y-2 rounded-lg border border-border p-3">
            <h2 className="text-sm font-semibold">{title}</h2>
            {children}
        </section>
    );
}

function LockedNote({ updatedAt }: { updatedAt?: string | null }) {
    return (
        <p className="text-[11px] text-muted-foreground">
            Bloqueado: la sync lo sobrescribe.
            {updatedAt ? ` Actualizado el ${formatDateTime(updatedAt)}.` : ''}
        </p>
    );
}

export function CatalogFichaEditorPage() {
    const { id, productId: productParam } = useParams<{ id?: string; productId?: string }>();
    const location = useLocation();
    const productsHref = storeProductsListHrefFrom(location.state) ?? '/store/products';
    const productId = Number(productParam ?? id);
    const validId = Number.isInteger(productId) && productId > 0;
    const detailQ = useCatalogProductDetail(validId ? productId : null);
    const saveMut = useUpdateCatalogProductContent();
    const priceMut = useSetCatalogPriceOverride();
    const activeMut = useSetCatalogProductActive();
    const [form, setForm] = useState<FormState | null>(null);
    const [baseline, setBaseline] = useState('');
    const [hydratedId, setHydratedId] = useState<number | null>(null);
    const [copied, setCopied] = useState(false);
    const [allowSupplierMention, setAllowSupplierMention] = useState(false);
    const [priceDraft, setPriceDraft] = useState('');
    const [confirm, setConfirm] = useState<ConfirmKind | null>(null);
    const supplierTerms = useSupplierMentionTerms();
    const detail = detailQ.data ?? null;

    if (detail && detail.id !== hydratedId) {
        const next = buildForm(detail);
        setHydratedId(detail.id);
        setForm(next);
        setBaseline(contentDraftSnapshot(next));
        setAllowSupplierMention(false);
        setPriceDraft(detail.precioManual ? String(detail.precio ?? '') : '');
    }

    const dirty = form != null && baseline !== '' && contentDraftIsDirty(baseline, form);
    const leave = useWarnUnsavedLeave(dirty);
    useEffect(() => {
        if (!copied) return;
        const timer = window.setTimeout(() => setCopied(false), 2000);
        return () => window.clearTimeout(timer);
    }, [copied]);

    const set = (key: keyof FormState) =>
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setForm((prev) => (prev ? { ...prev, [key]: event.target.value } : prev));

    const previewFlags = useMemo(() => {
        if (!form) return {};
        return {
            hasEditorial: Boolean(form.editorial.trim()),
            hasVeredicto: Boolean(form.veredicto.trim()),
            hasSeo: Boolean(form.seoTitle.trim()),
            hasDescripcion: Boolean(form.descripcionLarga.trim() || form.descripcionCorta.trim()),
        };
    }, [form]);

    const supplierMention = useMemo(
        () => (form ? findSupplierMention(contentValues(form), supplierTerms) : null),
        [form, supplierTerms],
    );
    const fieldNotices = useMemo(
        () => (form ? assessFichaFields(form) : []),
        [form],
    );
    const seoTitleBlocked = fichaSaveBlocked(fieldNotices);

    const onSave = async () => {
        if (!form || !validId) return;
        if (!form.nombre.trim()) {
            notify({ type: 'err', text: 'El nombre no puede quedar vacío' });
            return;
        }
        if (seoTitleBlocked) {
            notify({ type: 'err', text: 'El SEO title pasa de 60 caracteres y la web lo recorta. Acórtalo antes de guardar.' });
            return;
        }
        if (patchHasInternalLeak(contentValues(form))) {
            notify({ type: 'err', text: 'Hay notas internas (CHAT/VB/pasada). Quítalas antes de guardar.' });
            return;
        }
        if (supplierMention && !allowSupplierMention) {
            notify({
                type: 'err',
                text: `El texto nombra al proveedor o distribuidor (${supplierMention}). Quítalo o marca «guardar igualmente».`,
            });
            return;
        }
        const shippingPackageType = shippingPackagePatchValue(
            form.shippingPackageLoaded,
            form.shippingPackageType,
        );
        const patch: CatalogContentPatch = {
            nombre: form.nombre.trim(),
            ...(supplierMention && allowSupplierMention ? { allow_supplier_mention: true } : {}),
            editorial: form.editorial,
            veredicto_aipeces: form.veredicto,
            descripcion_corta: form.descripcionCorta,
            descripcion_larga: form.descripcionLarga,
            seo_title: form.seoTitle,
            seo_description: form.seoDescription,
            imagen_alt: form.imagenAlt,
            badges: splitList(form.badges),
            pesca_tags: splitList(form.pescaTags),
            especies_relacionadas: splitList(form.especies),
            guias_relacionadas: splitList(form.guias),
            ...(shippingPackageType === undefined ? {} : { shipping_package_type: shippingPackageType }),
            devolucion_recogida_sin_coste: form.devolucionRecogidaSinCoste,
            faq: form.faq
                .map((item) => ({ q: (item.q || '').trim(), a: (item.a || '').trim() }))
                .filter((item) => item.q && item.a),
            specs: rowsToSpecs(form.specs),
            quality_status: form.qualityStatus as CatalogQualityStatus,
            categoria_ruta: form.categoriaRuta.trim(),
            coleccion_slugs: splitList(form.coleccionSlugs),
        };
        try {
            await saveMut.mutateAsync({ productId, patch });
            setBaseline(contentDraftSnapshot({
                ...form,
                shippingPackageLoaded: form.shippingPackageType,
            }));
            setForm((prev) => (prev ? { ...prev, shippingPackageLoaded: prev.shippingPackageType } : prev));
            notify({ type: 'ok', text: 'Ficha guardada' });
        } catch (error: unknown) {
            notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        }
    };

    const copyPrompt = async () => {
        if (!form || !detail) return;
        const text = buildProductAiPrompt({
            seoSlug: detail.seoSlug,
            nombre: form.nombre,
            marca: publishedBrand(detail),
            categoria: detail.categoria,
            specs: rowsToSpecs(form.specs),
            editorial: form.editorial,
            veredicto: form.veredicto,
            descripcionLarga: form.descripcionLarga,
            seoTitle: form.seoTitle,
        });
        await navigator.clipboard.writeText(text);
        setCopied(true);
    };

    const applyConfirm = async () => {
        if (!validId || !confirm) return;
        try {
            if (confirm === 'price-set') {
                const precio = Number(priceDraft.trim().replace(',', '.'));
                if (!Number.isFinite(precio) || precio <= 0) {
                    notify({ type: 'err', text: 'El precio manual tiene que ser mayor que 0' });
                    return;
                }
                await priceMut.mutateAsync({ productId, precio });
                notify({ type: 'ok', text: 'Precio manual fijado' });
            } else if (confirm === 'price-unlock') {
                await priceMut.mutateAsync({ productId, unlock: true });
                setPriceDraft('');
                notify({ type: 'ok', text: 'Precio manual liberado' });
            } else {
                const isActive = confirm === 'activate';
                await activeMut.mutateAsync({ productId, isActive });
                notify({ type: 'ok', text: isActive ? 'Producto activado' : 'Producto desactivado' });
            }
            setConfirm(null);
        } catch (error: unknown) {
            notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        }
    };

    if (!validId) {
        return <p className="p-6 text-sm text-destructive">Identificador de producto no válido.</p>;
    }
    if (detailQ.isLoading || !form || !detail) {
        if (detailQ.isError || (detailQ.isSuccess && !detailQ.data)) {
            return (
                <p className="p-6 text-sm text-destructive">
                    {(detailQ.error as Error)?.message || 'No se encontró el producto'}
                </p>
            );
        }
        return (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Cargando ficha…
            </div>
        );
    }

    const d = detail;
    const imageUrl = catalogImageUrl(d.imagenBlobPath, d.imagenPrincipal, d.sourceImageUrl);
    const syncAt = d.supplierLastOkAt ?? d.lastSyncedAt;
    const syncStale = isStaleSync(syncAt, catalogSyncStaleMaxHours(d.supplierSlug));
    const min30 = omnibusMinPrice(
        (d.priceHistory ?? []).map((row) => ({ precio: Number(row.precio), recordedAt: row.recordedAt })),
        new Date(),
        d.precio,
    );
    const cost = d.costPrice == null || !Number.isFinite(Number(d.costPrice)) ? null : Number(d.costPrice);
    const margin = cost == null || !Number.isFinite(Number(d.precio)) ? null : Number(d.precio) - cost;
    const packageOptions = [
        { value: '', label: 'Automático (según categoría)' },
        { value: 'package_1kg', label: 'estándar' },
        { value: 'rods', label: 'rods' },
        { value: 'rods_long', label: 'rods_long' },
        ...(form.shippingPackageType === 'package_2kg'
            ? [{ value: 'package_2kg', label: 'package_2kg' }]
            : []),
    ];
    const packageLabel = packageOptions.find((option) => option.value === form.shippingPackageType)?.label
        ?? 'Automático (según categoría)';
    const collectionLabel = (d.colecciones ?? [])
        .map((item) => item.title || item.slug)
        .filter(Boolean)
        .join(', ');

    return (
        <div data-page="store-product-detail" data-product-id={d.id} data-seo-slug={d.seoSlug}>
            <ResourceDetail
                crumbs={[
                    { label: 'Productos', to: productsHref },
                    { label: form.nombre || 'Producto' },
                ]}
                title={form.nombre || 'Producto'}
                unsavedMessage={dirty ? 'Cambios sin guardar' : null}
                onDiscard={() => {
                    if (!baseline) return;
                    setForm(JSON.parse(baseline) as FormState);
                }}
                badge={(
                    <span className="flex flex-wrap items-center gap-2">
                        <FichaStatusBadge status={form.qualityStatus} />
                        <FichaCompleteness flags={previewFlags} />
                        {!d.isActive ? <StatusBadge status="inactive" label="No se vende" /> : null}
                    </span>
                )}
                actions={(
                    <>
                        <Button type="button" size="sm" onClick={() => void onSave()} disabled={saveMut.isPending || seoTitleBlocked}>
                            {saveMut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                            Guardar
                        </Button>
                        <a
                            href={publicProductUrl(d.seoSlug)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-muted/50"
                        >
                            <ExternalLink className="h-4 w-4" />
                            Ver en la tienda
                        </a>
                        <Button type="button" variant="outline" size="sm" onClick={() => void copyPrompt()}>
                            <Copy className="mr-1 h-4 w-4" />
                            {copied ? 'Prompt copiado' : 'Copiar prompt IA'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirm(d.isActive ? 'deactivate' : 'activate')}
                        >
                            {d.isActive ? 'Desactivar' : 'Activar'}
                        </Button>
                    </>
                )}
                aside={(
                    <div className="space-y-3">
                        <SideBlock title="Publicación">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button type="button" variant="outline" size="sm" className="w-full justify-between">
                                        {QUALITY_LABELS[form.qualityStatus as CatalogQualityStatus] ?? 'Borrador'}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    {QUALITY_ORDER.map((status) => (
                                        <DropdownMenuItem
                                            key={status}
                                            onSelect={() => setForm((prev) => (prev ? { ...prev, qualityStatus: status } : prev))}
                                        >
                                            {QUALITY_LABELS[status]}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <p className="text-[11px] text-muted-foreground">{QUALITY_HINTS[form.qualityStatus as CatalogQualityStatus]}</p>
                            <p className="text-[11px] text-muted-foreground">Se guarda con el botón Guardar.</p>
                        </SideBlock>

                        <SideBlock title="Origen">
                            <p className="text-sm">{d.supplierName || d.supplierSlug}</p>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span>{syncAt ? formatDateTime(syncAt) : 'Sin sync'}</span>
                                {syncStale ? (
                                    <StatusBadge status="unsynced" variant={STATUS_TONE_VARIANT.aviso} label="Caducada" />
                                ) : null}
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Precio del proveedor</p>
                                <p className="text-sm">
                                    {d.precioManual
                                        ? 'Fijado a mano: el feed no guarda otro precio'
                                        : formatProductPrice(d.precio, d.moneda)}
                                </p>
                                <LockedNote updatedAt={d.lastSyncedAt} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="ff-precio-manual">Precio manual</Label>
                                <Input
                                    id="ff-precio-manual"
                                    inputMode="decimal"
                                    value={priceDraft}
                                    onChange={(event) => setPriceDraft(event.target.value)}
                                    placeholder="12,90"
                                />
                                <div className="flex gap-2">
                                    <Button type="button" size="sm" variant="outline" onClick={() => setConfirm('price-set')}>
                                        Fijar
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={!d.precioManual}
                                        onClick={() => setConfirm('price-unlock')}
                                    >
                                        Liberar
                                    </Button>
                                </div>
                            </div>
                        </SideBlock>

                        <SideBlock title="Precio y margen">
                            <p className="text-sm">PVP {formatProductPrice(d.precio, d.moneda)}</p>
                            <p className="text-sm">Coste {cost == null ? '—' : formatEur(cost)}</p>
                            <p className="text-sm">Margen {margin == null ? '—' : formatEur(margin)}</p>
                            <p className="text-sm">
                                Mínimo 30 días {min30 == null ? '—' : formatProductPrice(min30, d.moneda)}
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                                Omnibus: el menor precio en vigor durante los últimos 30 días, según el historial.
                            </p>
                        </SideBlock>

                        <SideBlock title="Organización">
                            <CatalogProductBrandField
                                productId={d.id}
                                current={publishedBrand(d)}
                                pendingEditorial={Boolean(d.marcaEditorial?.trim())}
                            />
                            <div>
                                <p className="text-xs text-muted-foreground">Categoría</p>
                                <p className="text-sm">{d.categoria || '—'}</p>
                                <LockedNote updatedAt={d.lastSyncedAt} />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="ff-categoria-ruta">Ruta de categoría</Label>
                                <Input
                                    id="ff-categoria-ruta"
                                    value={form.categoriaRuta}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setForm((prev) => (
                                        prev ? { ...prev, categoriaRuta: event.target.value } : prev
                                    ))}
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    La sync no pisa este campo. En un proveedor de fichero, «Cañas» entra en /tienda/canas.
                                </p>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="ff-colecciones">Colecciones</Label>
                                <Input
                                    id="ff-colecciones"
                                    value={form.coleccionSlugs}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setForm((prev) => (
                                        prev ? { ...prev, coleccionSlugs: event.target.value } : prev
                                    ))}
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    Slugs de hubs activos, separados por comas. Ahora: {collectionLabel || '—'}.
                                </p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Paquete de envío</p>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button type="button" variant="outline" size="sm" className="w-full justify-between">
                                            {packageLabel}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        {packageOptions.map((option) => (
                                            <DropdownMenuItem
                                                key={option.value || 'auto'}
                                                onSelect={() => setForm((prev) => (
                                                    prev ? { ...prev, shippingPackageType: option.value } : prev
                                                ))}
                                            >
                                                {option.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <p className="text-[11px] text-muted-foreground">
                                    Automático usa la categoría. Caña larga solo se envía a Península y Portugal.
                                </p>
                            </div>
                            <label className="flex items-start gap-2 text-sm" htmlFor="ff-free-return">
                                <input
                                    id="ff-free-return"
                                    type="checkbox"
                                    className="mt-1"
                                    checked={form.devolucionRecogidaSinCoste}
                                    onChange={(event) => setForm((prev) => (
                                        prev ? { ...prev, devolucionRecogidaSinCoste: event.target.checked } : prev
                                    ))}
                                />
                                <span>Recogida de la devolución sin coste para el cliente</span>
                            </label>
                        </SideBlock>

                        <SideBlock title="Cumplimiento">
                            <BrandCompliance brandName={publishedBrand(d)} />
                        </SideBlock>

                        <SideBlock title="Historial de precio y stock">
                            {(d.priceHistory ?? []).length === 0 ? (
                                <p className="text-sm text-muted-foreground">Sin cambios registrados.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {d.priceHistory.slice(0, 8).map((row) => (
                                        <li key={row.id} className="text-sm">
                                            <span className="tabular-nums">{formatProductPrice(row.precio, d.moneda)}</span>
                                            {' · '}
                                            {stockLabel(row.disponibilidad, null)}
                                            <span className="block text-[11px] text-muted-foreground">
                                                {formatDateTime(row.recordedAt)}
                                                {row.costPrice == null ? '' : ` · coste ${formatEur(Number(row.costPrice))}`}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <p className="text-[11px] text-muted-foreground">
                                Anota una línea cuando cambia la cifra, el stock o el coste.
                            </p>
                        </SideBlock>

                        <details className="rounded-lg border border-border p-3">
                            <summary className="cursor-pointer text-sm font-semibold">Avanzado</summary>
                            <dl className="mt-2 space-y-1 text-sm">
                                <div>
                                    <dt className="text-xs text-muted-foreground">Id externo</dt>
                                    <dd className="font-mono text-xs">{d.externalId || '—'}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">Slug</dt>
                                    <dd className="font-mono text-xs">{d.seoSlug}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">Referencia del proveedor</dt>
                                    <dd>{d.referencia || '—'}</dd>
                                </div>
                            </dl>
                        </details>
                    </div>
                )}
            >
                <div className="space-y-4">
                    <SupplierMentionWarning
                        mention={supplierMention}
                        override={allowSupplierMention}
                        onOverrideChange={setAllowSupplierMention}
                        idPrefix="ff"
                    />
                    <div className="space-y-1">
                        <Label htmlFor="ff-nombre">Nombre</Label>
                        <Input id="ff-nombre" value={form.nombre} onChange={set('nombre')} />
                        <FichaFieldNotices notices={noticesForField(fieldNotices, 'nombre')} />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-desc-corta">Descripción corta</Label>
                        <Textarea id="ff-desc-corta" rows={2} value={form.descripcionCorta} onChange={set('descripcionCorta')} />
                        <FichaFieldNotices notices={noticesForField(fieldNotices, 'descripcionCorta')} />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-desc-larga">Descripción larga</Label>
                        <Textarea id="ff-desc-larga" rows={5} value={form.descripcionLarga} onChange={set('descripcionLarga')} />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-editorial">Criterio AiPeces</Label>
                        <Textarea id="ff-editorial" rows={5} value={form.editorial} onChange={set('editorial')} />
                        <p className="text-[11px] text-muted-foreground">Enlaces: [texto](/blog/slug)</p>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-veredicto">Veredicto</Label>
                        <Textarea id="ff-veredicto" rows={3} value={form.veredicto} onChange={set('veredicto')} />
                    </div>
                    <div className="space-y-2">
                        <Label>Preguntas frecuentes</Label>
                        {form.faq.map((item, index) => (
                            <div key={index} className="space-y-1 rounded-lg border p-2">
                                <Input
                                    placeholder="Pregunta"
                                    value={item.q || ''}
                                    onChange={(event) => setForm((prev) => {
                                        if (!prev) return prev;
                                        const faq = prev.faq.map((row, rowIndex) => (
                                            rowIndex === index ? { ...row, q: event.target.value } : row
                                        ));
                                        return { ...prev, faq };
                                    })}
                                />
                                <Textarea
                                    rows={2}
                                    placeholder="Respuesta"
                                    value={item.a || ''}
                                    onChange={(event) => setForm((prev) => {
                                        if (!prev) return prev;
                                        const faq = prev.faq.map((row, rowIndex) => (
                                            rowIndex === index ? { ...row, a: event.target.value } : row
                                        ));
                                        return { ...prev, faq };
                                    })}
                                />
                            </div>
                        ))}
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setForm((prev) => (prev ? { ...prev, faq: [...prev.faq, { q: '', a: '' }] } : prev))}
                        >
                            Añadir pregunta
                        </Button>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-especies">Especies relacionadas</Label>
                        <Input
                            id="ff-especies"
                            value={form.especies}
                            onChange={set('especies')}
                            placeholder="lubina, dorada"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-guias">Guías relacionadas</Label>
                        <Input
                            id="ff-guias"
                            value={form.guias}
                            onChange={set('guias')}
                            placeholder="slugs separados por comas"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-tags">Etiquetas de pesca</Label>
                        <Input id="ff-tags" value={form.pescaTags} onChange={set('pescaTags')} placeholder="kayak, jigging" />
                    </div>
                    <div className="space-y-2">
                        <Label>Ficha técnica</Label>
                        {form.specs.map((row, index) => (
                            <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                                <Input
                                    placeholder="Campo"
                                    value={row.key}
                                    onChange={(event) => setForm((prev) => {
                                        if (!prev) return prev;
                                        const specs = prev.specs.map((item, itemIndex) => (
                                            itemIndex === index ? { ...item, key: event.target.value } : item
                                        ));
                                        return { ...prev, specs };
                                    })}
                                />
                                <Input
                                    placeholder="Valor"
                                    value={row.value}
                                    onChange={(event) => setForm((prev) => {
                                        if (!prev) return prev;
                                        const specs = prev.specs.map((item, itemIndex) => (
                                            itemIndex === index ? { ...item, value: event.target.value } : item
                                        ));
                                        return { ...prev, specs };
                                    })}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setForm((prev) => (
                                        prev ? { ...prev, specs: prev.specs.filter((_, itemIndex) => itemIndex !== index) } : prev
                                    ))}
                                >
                                    Quitar
                                </Button>
                            </div>
                        ))}
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setForm((prev) => (
                                prev ? { ...prev, specs: [...prev.specs, { key: '', value: '' }] } : prev
                            ))}
                        >
                            Añadir spec
                        </Button>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="ff-badges">Badges</Label>
                        <Input id="ff-badges" value={form.badges} onChange={set('badges')} placeholder="Más vendido, Novedad" />
                    </div>

                    <section className="space-y-2">
                        <h2 className="text-sm font-semibold">Multimedia</h2>
                        {imageUrl ? (
                            <img src={imageUrl} alt={form.imagenAlt || form.nombre} className="max-h-56 rounded-lg border object-contain" />
                        ) : (
                            <p className="text-sm text-muted-foreground">Sin imagen.</p>
                        )}
                        <LockedNote updatedAt={d.lastSyncedAt} />
                        {d.imageCheckStatus ? (
                            <p className="text-[11px] text-muted-foreground">
                                Estado de la imagen: {storeStatusLabel(d.imageCheckStatus)}
                            </p>
                        ) : null}
                        <Label htmlFor="ff-alt">Texto alternativo</Label>
                        <Input id="ff-alt" value={form.imagenAlt} onChange={set('imagenAlt')} />
                        <FichaFieldNotices notices={noticesForField(fieldNotices, 'imagenAlt')} />
                    </section>

                    <section className="space-y-2">
                        <h2 className="text-sm font-semibold">Variantes de origen</h2>
                        {(d.variants ?? []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">Sin variantes.</p>
                        ) : (
                            <ul className="space-y-2">
                                {d.variants?.map((variant) => (
                                    <li key={variant.id} className="rounded-lg border p-2 text-sm">
                                        <p className="font-mono text-xs">{variant.sku || variant.externalId || variant.id}</p>
                                        <p>
                                            {formatProductPrice(variant.precio, d.moneda)}
                                            {' · '}
                                            {stockLabel(variant.disponibilidadEfectiva || variant.disponibilidad, null)}
                                        </p>
                                        {attributeText(variant.atributos) ? (
                                            <p className="text-xs text-muted-foreground">{attributeText(variant.atributos)}</p>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <CatalogAvailabilityEditor detail={d} />
                    </section>

                    <section className="space-y-2">
                        <h2 className="text-sm font-semibold">SEO</h2>
                        <div className="rounded-lg border bg-white p-3 dark:bg-zinc-950">
                            <p className="truncate text-xs text-emerald-700">{publicProductUrl(d.seoSlug)}</p>
                            <p className="text-lg leading-snug text-blue-800 dark:text-blue-300">
                                {form.seoTitle || form.nombre || 'Sin título'}
                            </p>
                            <p className="line-clamp-2 text-sm text-zinc-600 dark:text-zinc-300">
                                {form.seoDescription || form.descripcionCorta || 'Sin descripción'}
                            </p>
                        </div>
                        <Label htmlFor="ff-seo-title">SEO title</Label>
                        <Input id="ff-seo-title" value={form.seoTitle} onChange={set('seoTitle')} />
                        <p className="text-[10px] text-muted-foreground">{form.seoTitle.length}/60</p>
                        <FichaFieldNotices notices={noticesForField(fieldNotices, 'seoTitle')} />
                        <Label htmlFor="ff-seo-desc">SEO description</Label>
                        <Textarea id="ff-seo-desc" rows={2} value={form.seoDescription} onChange={set('seoDescription')} />
                        <p className="text-[10px] text-muted-foreground">{form.seoDescription.length}/155</p>
                        <FichaFieldNotices notices={noticesForField(fieldNotices, 'seoDescription')} />
                    </section>

                    <details className="rounded-lg border p-3">
                        <summary className="cursor-pointer text-sm font-semibold">Vista previa de la ficha</summary>
                        <div className="mt-3">
                            <ProductFichaPreview
                                model={{
                                    nombre: form.nombre,
                                    seoSlug: d.seoSlug,
                                    marca: publishedBrand(d),
                                    precio: d.precio,
                                    moneda: d.moneda,
                                    disponibilidad: d.disponibilidad,
                                    disponibilidadTexto: d.disponibilidadTexto,
                                    imagenBlobPath: d.imagenBlobPath,
                                    imagenPrincipal: d.imagenPrincipal,
                                    sourceImageUrl: d.sourceImageUrl,
                                    imagenAlt: form.imagenAlt,
                                    editorial: form.editorial,
                                    descripcionLarga: form.descripcionLarga,
                                    descripcionCorta: form.descripcionCorta,
                                    veredicto: form.veredicto,
                                    pescaTags: splitList(form.pescaTags),
                                    specs: rowsToSpecs(form.specs),
                                    faq: form.faq as CatalogFaqItem[],
                                    seoTitle: form.seoTitle,
                                    seoDescription: form.seoDescription,
                                }}
                            />
                        </div>
                    </details>
                </div>
            </ResourceDetail>

            <AdminConfirmDialog
                open={leave.pendingHref != null}
                title="Hay cambios sin guardar"
                description="Si sales ahora, se pierden los cambios de esta ficha."
                confirmLabel="Salir sin guardar"
                variant="danger"
                onConfirm={leave.confirmLeave}
                onCancel={leave.cancelLeave}
            />
            <AdminConfirmDialog
                open={confirm === 'activate' || confirm === 'deactivate'}
                title={confirm === 'deactivate' ? '¿Desactivar este producto?' : '¿Activar este producto?'}
                description={confirm === 'deactivate'
                    ? 'Dejará de venderse en la tienda. El contenido de la ficha se conserva.'
                    : 'Volverá a poder venderse en la tienda.'}
                confirmLabel={confirm === 'deactivate' ? 'Desactivar' : 'Activar'}
                variant={confirm === 'deactivate' ? 'danger' : 'default'}
                confirmDisabled={activeMut.isPending}
                onConfirm={() => void applyConfirm()}
                onCancel={() => setConfirm(null)}
            />
            <AdminConfirmDialog
                open={confirm === 'price-set' || confirm === 'price-unlock'}
                title={confirm === 'price-unlock' ? '¿Liberar el precio manual?' : '¿Fijar el precio manual?'}
                description={confirm === 'price-unlock'
                    ? 'La próxima sync podrá volver a actualizar el precio.'
                    : 'La sync dejará de pisar este precio hasta que lo liberes.'}
                confirmLabel={confirm === 'price-unlock' ? 'Liberar' : 'Fijar'}
                confirmDisabled={priceMut.isPending}
                onConfirm={() => void applyConfirm()}
                onCancel={() => setConfirm(null)}
            />
        </div>
    );
}
